import { NodeTypes as FlowNodeTypes } from "@xyflow/react";
import type { ScheduleConfig } from "@quake/isomorphic/schemas/automation-nodes.schema";

export type IteratorSettings = {
  collection?: string;
  filter?: string | null;
  maxIterations?: number | null;
  stopOnFailure?: boolean | null;
};

export type EntityBatchSettings = {
  model_id?: string | null;
  filters?: Record<string, unknown>;
  batchSize?: number | null;
  maxBatches?: number | null;
  stopOnFailure?: boolean | null;
};

export const ENTITY_BATCH_NODE_TYPES = [
  "candidateRecordBatch",
  "contactRecordBatch",
  "clientRecordBatch",
  "jobRecordBatch",
  "applicationRecordBatch",
  "customEntityRecordBatch",
  "recordBatch",
] as const;

export type EntityBatchNodeType = (typeof ENTITY_BATCH_NODE_TYPES)[number];

export function isEntityBatchNodeType(type?: string | null): type is EntityBatchNodeType {
  return ENTITY_BATCH_NODE_TYPES.includes(type as EntityBatchNodeType);
}

export type NodeData = {
  label: string;
  note?: string;
  readableId: string; // Backend ID for referencing in expressions and backend operations
  recordId?: string;
  icon?: string;
  type: keyof NodeTypes;
  triggerType?: "manual" | "interval" | "webhook" | "event" | "email" | "app";
  // Schedule configuration for interval triggers
  schedule?: ScheduleConfig;
  // App trigger fields
  triggerId?: string;
  triggerAppId?: string;
  triggerInputs?: Record<string, string>;
  // Email trigger fields
  allowedEmails?: string;
  triggerEmail?: string;
  isExecuting?: boolean;
  // Webhook trigger fields
  webhookToken?: string;
  webhookUrl?: string;
  runtime?: "sync" | "async";
  authenticationHeader?: {
    required: boolean;
    value: string;
    key: string;
  };
  // Platform events trigger fields
  source?: string;
  event?: {
    record: string;
    recordId: string;
    events: string[];
    runMode?: "automatic" | "manual";
  };
  // Manual trigger fields
  recordTypes?: string[];
  input_schema?: any;
  input_data?: any;
  output_schema?: any;
  output?: any;
  error?: { message: string };
  app?: {
    id: string;
    origin_id: string;
    installed_id?: string;
    name: string;
    logo_url?: string;
  };
  action?: {
    id: string;
    name: string;
    description: string;
    input_schema: any;
    output_schema: any;
    parameters?: any;
    mockOutput?: any;
    useMockOutput?: boolean;
    logo_url?: string;
  };
  // Condition-specific
  expression?: string;
  // Iterator-specific
  iterator?: IteratorSettings;
  // Entity batch-specific
  entityBatch?: EntityBatchSettings;
  position?: {
    x: number;
    y: number;
  };
  // Parent-child relationship for container nodes
  parentNode?: string; // ID of parent iterator
  isIteratorContainer?: boolean; // Flag for iterator nodes
  childNodes?: string[]; // Track children for auto-sizing
};

export type CustomNodeTypes = {
  trigger: React.ComponentType<any>;
  action: React.ComponentType<any>;
  ghost: React.ComponentType<any>;
  condition: React.ComponentType<any>;
  iterator: React.ComponentType<any>;
  candidateRecordBatch: React.ComponentType<any>;
  contactRecordBatch: React.ComponentType<any>;
  clientRecordBatch: React.ComponentType<any>;
  jobRecordBatch: React.ComponentType<any>;
  applicationRecordBatch: React.ComponentType<any>;
  customEntityRecordBatch: React.ComponentType<any>;
  recordBatch: React.ComponentType<any>;
};

export type NodeTypes = FlowNodeTypes & CustomNodeTypes;

export type TriggerType = "manual" | "interval" | "webhook" | "event" | "email" | "condition" | "app";
